import React, { useEffect, useMemo, useState } from "react";
import { Col, Row, FormGroup, ControlLabel } from "react-bootstrap";
import { useHistory, useParams } from "react-router-dom";
import { InventoryTab, inventoryTabTarget } from './inventoryTabNavigation';
import { useTranslation } from "react-i18next";
import Card from "../../../components/Cards/Card";
import Loader from "../../../components/Common/Loader";
import { CustomSelect } from "../../../components/CustomControls/CustomSelect";
import { CustomFormControl } from "../../../components/CustomControls/CustomFormControl";
import { useLaboratoryProtocolInventory } from "../Hooks/useLaboratoryProtocolInventory";
import { EnumValue } from "../../../api/Common/Util";
import InventoryItemsService from "../../../api/Inventory/InventoryItemsService";
import { ErrorMessage, Success } from "../../../components/Notifications/Notifications";
import useConfirmationModal from "../../../components/Modals/ConfirmationModal/useConfirmationModal";

const DEFAULT_NAME = "IMP";

export default function CreateProtocolItemPage() {
    const history = useHistory();
    const { t } = useTranslation();
    const { itemId } = useParams();
    const isEdit = itemId != null;

    const [protocolState, handleLaboratoryChange] = useLaboratoryProtocolInventory();
    const [selectedLaboratory, setSelectedLaboratory] = useState(null);
    const [selectedProtocol, setSelectedProtocol] = useState(null);
    const [protocolExternalId, setProtocolExternalId] = useState(null);
    const [name, setName] = useState(DEFAULT_NAME);
    const [selectedMedicationForm, setSelectedMedicationForm] = useState(null);
    const [kitQuantity, setKitQuantity] = useState("");
    const [isLoadingItem, setIsLoadingItem] = useState(isEdit);

    const [ConfirmationModal, modalActions] = useConfirmationModal();

    const medicationForms = useMemo(() => [
        new EnumValue("Injectable", t("inventory.commons.medicationFormInjectable")),
        new EnumValue("Cream", t("inventory.commons.medicationFormCream")),
        new EnumValue("Pill", t("inventory.commons.medicationFormPill")),
    ], [t]);

    useEffect(() => {
        if (selectedLaboratory === null) return;
        setSelectedProtocol(null);
        handleLaboratoryChange(selectedLaboratory.Value);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedLaboratory]);

    useEffect(() => {
        if (!isEdit) return;
        let active = true;
        (async () => {
            try {
                const result = await InventoryItemsService.GetById({ itemId: Number(itemId) });
                if (!active) return;
                const item = result.Value;
                setProtocolExternalId(item?.externalId ?? null);
                setName(item?.presentation ?? DEFAULT_NAME);
                setKitQuantity(item?.kitQuantity != null ? `${item.kitQuantity}` : "");
                const form = medicationForms.find(f => f.Value === item?.medicationForm) ?? null;
                setSelectedMedicationForm(form);
            } catch (error) {
                ErrorMessage(t("errors.serverError"));
            } finally {
                if (active) setIsLoadingItem(false);
            }
        })();
        return () => { active = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemId]);

    const validate = () => {
        if (!isEdit && selectedProtocol === null) {
            ErrorMessage(t("inventory.commons.protocolRequired"));
            return false;
        }
        if (selectedMedicationForm === null) {
            ErrorMessage(t("inventory.commons.medicationFormRequired"));
            return false;
        }
        const quantity = Number(kitQuantity);
        if (isNaN(quantity) || quantity <= 0) {
            ErrorMessage(t("inventory.commons.kitQuantityMustBePositive"));
            return false;
        }
        return true;
    };

    const submitRequest = async () => {
        if (isEdit) {
            return InventoryItemsService.UpdateProtocol({
                itemId: Number(itemId),
                name: name?.trim() ? name.trim() : null,
                medicationForm: selectedMedicationForm.Value,
                kitQuantity: Number(kitQuantity),
            });
        }
        return InventoryItemsService.CreateProtocol({
            protocolId: selectedProtocol.Value,
            name: name?.trim() ? name.trim() : null,
            medicationForm: selectedMedicationForm.Value,
            kitQuantity: Number(kitQuantity),
        });
    };

    const handleSubmit = () => {
        if (!validate()) return;

        modalActions.Show({
            title: t("inventory.movements.confirmations.completeTitle"),
            textContent: t("inventory.movements.confirmations.completeTextContent"),
            buttonText: t("buttons.confirm"),
            useComment: false,
            onConfirmation: async () => {
                try {
                    const result = await submitRequest();
                    if (result.Succeeded) {
                        Success(t(isEdit
                            ? "inventory.commons.protocolItemUpdatedSuccessfully"
                            : "inventory.commons.protocolItemCreatedSuccessfully"));
                        history.push(inventoryTabTarget(InventoryTab.protocolInventory));
                    } else {
                        ErrorMessage(result.Errors?.length > 0 ? result.Errors[0] : t("inventory.commons.protocolItemDuplicate"));
                    }
                } catch (error) {
                    if (error.response && error.response.status === 400) {
                        const validationErrors = error.response.data?.Errors;
                        ErrorMessage(validationErrors?.length > 0 ? validationErrors[0] : t("inventory.commons.protocolItemDuplicate"));
                    } else {
                        ErrorMessage(t("errors.serverError"));
                    }
                }
            },
        });
    };

    const handleCancel = () => {
        history.push(inventoryTabTarget(InventoryTab.protocolInventory));
    };

    const isProtocolSelectDisabled = selectedLaboratory === null || !(protocolState.protocols.length > 0);

    return (<>
        <Card
            header={<div>
                <h4>{t(isEdit ? "inventory.commons.editProtocolItem" : "inventory.commons.protocolItem")}</h4>
            </div>}
            content={<Loader isLoading={isLoadingItem}>
                <form onSubmit={(e) => e.preventDefault()}>
                    {isEdit
                        ? <Row>
                            <Col sm={12} md={6}>
                                <FormGroup>
                                    <ControlLabel htmlFor="protocol_item_protocol">{t("commons.protocols")}</ControlLabel>
                                    <input id="protocol_item_protocol" disabled className="form-control"
                                        value={protocolExternalId ?? ""} />
                                </FormGroup>
                            </Col>
                        </Row>
                        : <Row>
                            <Col sm={12} md={6}>
                                <FormGroup>
                                    <ControlLabel htmlFor="select_laboratory">{t("commons.laboratories")}</ControlLabel>
                                    <CustomSelect
                                        name="select_laboratory"
                                        value={selectedLaboratory}
                                        onChange={(_name, value) => setSelectedLaboratory(value)}
                                        options={protocolState.laboratories}
                                        getOptionLabel={(option) => option?.Name}
                                        getOptionValue={(option) => option?.Value}
                                        placeholder={t("commons.select")}
                                        isLoading={protocolState.isLoading}
                                    />
                                </FormGroup>
                            </Col>
                            <Col sm={12} md={6}>
                                <FormGroup>
                                    <ControlLabel htmlFor="select_protocol">{t("commons.protocols")}</ControlLabel>
                                    <CustomSelect
                                        name="select_protocol"
                                        value={selectedProtocol}
                                        onChange={(_name, value) => setSelectedProtocol(value)}
                                        options={protocolState.protocols}
                                        getOptionLabel={(option) => option?.Name}
                                        getOptionValue={(option) => option?.Value}
                                        isDisabled={isProtocolSelectDisabled}
                                        placeholder={t("commons.select")}
                                        isLoading={protocolState.isLoading}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>}
                    <Row>
                        <Col sm={12} md={6}>
                            <FormGroup>
                                <ControlLabel htmlFor="protocol_item_name">{t("commons.name")}</ControlLabel>
                                <CustomFormControl
                                    name="protocol_item_name"
                                    type="text"
                                    value={name}
                                    onChange={(_name, value) => setName(value)}
                                />
                            </FormGroup>
                        </Col>
                        <Col sm={12} md={6}>
                            <FormGroup>
                                <ControlLabel htmlFor="select_medication_form">{t("inventory.commons.medicationForm")}</ControlLabel>
                                <CustomSelect
                                    name="select_medication_form"
                                    value={selectedMedicationForm}
                                    onChange={(_name, value) => setSelectedMedicationForm(value)}
                                    options={medicationForms}
                                    getOptionLabel={(option) => option?.Name}
                                    getOptionValue={(option) => option?.Value}
                                    placeholder={t("commons.select")}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col sm={12} md={6}>
                            <FormGroup>
                                <ControlLabel htmlFor="protocol_item_kit_quantity">{t("inventory.commons.kitQuantity")}</ControlLabel>
                                <CustomFormControl
                                    name="protocol_item_kit_quantity"
                                    type="number"
                                    value={kitQuantity}
                                    onChange={(_name, value) => setKitQuantity(value)}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: "1rem", marginTop: "1rem" }}>
                        <button className="btn btn-secondary" type="button" onClick={handleCancel}>{t("buttons.back")}</button>
                        <button className="btn btn-success" type="button" onClick={handleSubmit}>{t("buttons.save")}</button>
                    </div>
                </form>
            </Loader>}>
        </Card>
        <ConfirmationModal />
    </>);
}
